import { customFetch } from '~/utils/customFetch';
import { ErrorObjectString } from '~/utils/typescriptHelpers';
import {
  AnalyticsStatsApiError,
  flattenAnalyticsStatsErrorResponse,
} from '~/views/Dashboard/AnalyticsStats/analyticsStatsErrorUtils';
import {
  statScheduleListItemSchema,
  statScheduleRunOnceBodySchema,
  statScheduleRunOnceQueuedResponseSchema,
  statScheduleUpsertBodySchema,
  statSchedulesListResponseSchema,
  type StatScheduleListItem,
  type StatScheduleRunOnceBody,
  type StatScheduleRunOnceQueuedResponse,
  type StatScheduleUpsertBody,
} from './analyticsStatScheduleSchema';

type ResponseData = StatScheduleListItem[] | ErrorObjectString;

export async function fetchAnalyticsStatSchedules(
  accountId: number,
  accountType: 'client' | 'partner',
  options?: { statKey?: string; signal?: AbortSignal },
): Promise<StatScheduleListItem[]> {
  const params: Record<string, string> | undefined =
    options?.statKey !== undefined && options.statKey !== ''
      ? { statKey: options.statKey }
      : undefined;

  const { data } = await customFetch.get<ResponseData>(
    `/authenticated/${accountType}/${accountId}/analytics/schedules`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
      useNodeEndpoint: true,
      signal: options?.signal,
      ...(params ? { params } : {}),
    },
  );

  if ('error' in data) {
    throw new Error(data.error);
  }

  return statSchedulesListResponseSchema.parse(data);
}

function schedulesCollectionPath(accountType: 'client' | 'partner', accountId: number) {
  return `/authenticated/${accountType}/${accountId}/analytics/schedules`;
}

function schedulePath(
  accountType: 'client' | 'partner',
  accountId: number,
  schedulePublicId: string,
) {
  return `${schedulesCollectionPath(accountType, accountId)}/${schedulePublicId}`;
}

function scheduleTargetPath(
  accountType: 'client' | 'partner',
  accountId: number,
  schedulePublicId: string,
  targetPublicId: string,
) {
  return `${schedulePath(accountType, accountId, schedulePublicId)}/targets/${targetPublicId}`;
}

function parseScheduleMutationResponse(data: unknown): StatScheduleListItem | undefined {
  if (data == null) return undefined;
  if (typeof data === 'object' && Object.keys(data as object).length === 0) return undefined;
  return statScheduleListItemSchema.parse(data);
}

/** `POST …/analytics/schedules/{schedulePublicId}/disable` */
export async function postAnalyticsStatScheduleDisable(
  accountId: number,
  accountType: 'client' | 'partner',
  schedulePublicId: string,
  options?: { signal?: AbortSignal },
): Promise<StatScheduleListItem | undefined> {
  const { data, status } = await customFetch.post<unknown>(
    `${schedulePath(accountType, accountId, schedulePublicId)}/disable`,
    JSON.stringify({}),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      throwOnHTTPError: false,
      signal: options?.signal,
    },
  );

  if (status >= 400) {
    throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
  }
  return parseScheduleMutationResponse(data);
}

/** `POST …/analytics/schedules/{schedulePublicId}/enable` */
export async function postAnalyticsStatScheduleEnable(
  accountId: number,
  accountType: 'client' | 'partner',
  schedulePublicId: string,
  options?: { signal?: AbortSignal },
): Promise<StatScheduleListItem | undefined> {
  const { data, status } = await customFetch.post<unknown>(
    `${schedulePath(accountType, accountId, schedulePublicId)}/enable`,
    JSON.stringify({}),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      throwOnHTTPError: false,
      signal: options?.signal,
    },
  );

  if (status >= 400) {
    throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
  }
  return parseScheduleMutationResponse(data);
}

/** `DELETE …/analytics/schedules/{schedulePublicId}` */
export async function deleteAnalyticsStatSchedule(
  accountId: number,
  accountType: 'client' | 'partner',
  schedulePublicId: string,
  options?: { signal?: AbortSignal },
): Promise<void> {
  const { data, status } = await customFetch.delete<unknown>(
    schedulePath(accountType, accountId, schedulePublicId),
    {
      useNodeEndpoint: true,
      throwOnHTTPError: false,
      signal: options?.signal,
    },
  );

  if (status === 200 || status === 204) {
    return;
  }

  throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
}

/** `POST …/analytics/schedules/run-once` — validate config and queue a one-off job for the current user */
export async function postAnalyticsStatScheduleRunOnce(
  accountId: number,
  accountType: 'client' | 'partner',
  body: StatScheduleRunOnceBody,
  options?: { signal?: AbortSignal },
): Promise<StatScheduleRunOnceQueuedResponse> {
  const payload = statScheduleRunOnceBodySchema.parse(body);
  const { data, status } = await customFetch.post<unknown>(
    `${schedulesCollectionPath(accountType, accountId)}/run-once`,
    JSON.stringify(payload),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      throwOnHTTPError: false,
      signal: options?.signal,
    },
  );

  if (status === 202) {
    return statScheduleRunOnceQueuedResponseSchema.parse(data);
  }
  if (status >= 400) {
    throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
  }
  throw new AnalyticsStatsApiError(data);
}

/** `POST …/analytics/schedules` — create schedule */
export async function postAnalyticsStatSchedule(
  accountId: number,
  accountType: 'client' | 'partner',
  body: StatScheduleUpsertBody,
  options?: { signal?: AbortSignal },
): Promise<StatScheduleListItem> {
  const payload = statScheduleUpsertBodySchema.parse(body);
  const { data, status } = await customFetch.post<unknown>(
    schedulesCollectionPath(accountType, accountId),
    JSON.stringify(payload),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      throwOnHTTPError: false,
      signal: options?.signal,
    },
  );

  if (status >= 400) {
    throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
  }
  return statScheduleListItemSchema.parse(data);
}

/** `PUT …/analytics/schedules/{schedulePublicId}` — replace schedule */
export async function putAnalyticsStatSchedule(
  accountId: number,
  accountType: 'client' | 'partner',
  schedulePublicId: string,
  body: StatScheduleUpsertBody,
  options?: { signal?: AbortSignal },
): Promise<StatScheduleListItem> {
  const payload = statScheduleUpsertBodySchema.parse(body);
  const { data, status } = await customFetch.put<unknown>(
    schedulePath(accountType, accountId, schedulePublicId),
    JSON.stringify(payload),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      throwOnHTTPError: false,
      signal: options?.signal,
    },
  );

  if (status >= 400) {
    throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
  }
  return statScheduleListItemSchema.parse(data);
}

/** `POST …/analytics/schedules/{schedulePublicId}/targets/{targetPublicId}/disable` */
export async function postAnalyticsStatScheduleTargetDisable(
  accountId: number,
  accountType: 'client' | 'partner',
  schedulePublicId: string,
  targetPublicId: string,
  options?: { signal?: AbortSignal },
): Promise<StatScheduleListItem | undefined> {
  const { data, status } = await customFetch.post<unknown>(
    `${scheduleTargetPath(accountType, accountId, schedulePublicId, targetPublicId)}/disable`,
    JSON.stringify({}),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      throwOnHTTPError: false,
      signal: options?.signal,
    },
  );

  if (status >= 400) {
    throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
  }
  return parseScheduleMutationResponse(data);
}

/** `POST …/analytics/schedules/{schedulePublicId}/targets/{targetPublicId}/enable` */
export async function postAnalyticsStatScheduleTargetEnable(
  accountId: number,
  accountType: 'client' | 'partner',
  schedulePublicId: string,
  targetPublicId: string,
  options?: { signal?: AbortSignal },
): Promise<StatScheduleListItem | undefined> {
  const { data, status } = await customFetch.post<unknown>(
    `${scheduleTargetPath(accountType, accountId, schedulePublicId, targetPublicId)}/enable`,
    JSON.stringify({}),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      throwOnHTTPError: false,
      signal: options?.signal,
    },
  );

  if (status >= 400) {
    throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
  }
  return parseScheduleMutationResponse(data);
}

/** `DELETE …/analytics/schedules/{schedulePublicId}/targets/{targetPublicId}` */
export async function deleteAnalyticsStatScheduleTarget(
  accountId: number,
  accountType: 'client' | 'partner',
  schedulePublicId: string,
  targetPublicId: string,
  options?: { signal?: AbortSignal },
): Promise<void> {
  const { data, status } = await customFetch.delete<unknown>(
    scheduleTargetPath(accountType, accountId, schedulePublicId, targetPublicId),
    {
      useNodeEndpoint: true,
      throwOnHTTPError: false,
      signal: options?.signal,
    },
  );

  if (status === 200 || status === 204) {
    return;
  }

  throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
}
